import React, { useState } from 'react';
import { FiChevronRight, FiShield, FiXCircle, FiHelpCircle } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';

import { TopBar } from '@/design-system/components/organisms/TopBar';
import {
  BlocklistModal,
  type BlocklistData,
} from '@/features/onboarding/components/BlocklistModal';
import { BusinessRulesDrawer } from '@/features/onboarding/components/BusinessRulesDrawer';
import { ClientDetailView } from '@/features/onboarding/components/ClientDetailView';
import { DocumentModal } from '@/features/onboarding/components/DocumentModal';
import { RejectModal, type RejectData } from '@/features/onboarding/components/RejectModal';
import { Sidebar } from '@/features/onboarding/components/Sidebar';
import { usePreClientDetail, useDocumentUrl } from '@/features/onboarding/hooks';
import { ONBOARDING_SIDEBAR_ITEMS } from '@/shared/constants/sidebar.constants';

export const ClientDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [isBlocklistModalOpen, setIsBlocklistModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [isBusinessRulesOpen, setIsBusinessRulesOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState({ title: '', url: '' });
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);

  // Fetch client details from API
  const { data: client, isLoading, isError, error } = usePreClientDetail(clientId);

  // Hook to fetch document URL
  const { fetchDocumentUrl } = useDocumentUrl();

  const handleBlocklistConfirm = (_data: BlocklistData) => {
    // Aqui você faria a chamada API para adicionar à blocklist
    setIsBlocklistModalOpen(false);
  };

  const handleRejectConfirm = (_data: RejectData) => {
    // Aqui você faria a chamada API para reprovar a conta
    setIsRejectModalOpen(false);
  };

  const handleOpenDocumentAsync = async (title: string, documentId?: string) => {
    if (!documentId) {
      setCurrentDocument({ title, url: '' });
      setIsDocumentModalOpen(true);
      return;
    }

    setIsLoadingDocument(true);
    setCurrentDocument({ title, url: '' });
    setIsDocumentModalOpen(true);

    const response = await fetchDocumentUrl(documentId);
    if (response) {
      setCurrentDocument({ title, url: response.url });
    }
    setIsLoadingDocument(false);
  };

  const handleOpenDocument = (title: string, documentId?: string) => {
    void handleOpenDocumentAsync(title, documentId);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <TopBar />
        <div className="flex">
          <Sidebar items={ONBOARDING_SIDEBAR_ITEMS} className="w-64 min-h-[calc(100vh-140px)]" />
          <main className="flex-1 p-8">
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-neutral-600">Carregando dados do cliente...</span>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !client) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <TopBar />
        <div className="flex">
          <Sidebar items={ONBOARDING_SIDEBAR_ITEMS} className="w-64 min-h-[calc(100vh-140px)]" />
          <main className="flex-1 p-8">
            <div className="max-w-7xl mx-auto">
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                <p className="font-medium">Erro ao carregar dados do cliente</p>
                <p className="text-sm">{error?.message ?? 'Cliente não encontrado'}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  void navigate('/onboarding/pre-analysis');
                }}
                className="mt-4 px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              >
                Voltar para Pré-análise
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <TopBar />

      <div className="flex">
        {/* Sidebar */}
        <Sidebar items={ONBOARDING_SIDEBAR_ITEMS} className="w-64 min-h-[calc(100vh-140px)]" />

        {/* Main Content */}
        <main
          className={`flex-1 p-8 transition-all duration-300 ${isBusinessRulesOpen ? 'mr-112.5' : ''}`}
        >
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-2 text-sm text-neutral-600 mb-6">
              <button
                type="button"
                onClick={() => {
                  void navigate('/onboarding/pre-analysis');
                }}
                className="hover:text-purple-600 transition-colors"
              >
                Pré análise
              </button>
              <FiChevronRight className="w-4 h-4" />
              <span className="text-neutral-800 font-medium">Cliente</span>
            </div>

            {/* Client Detail View */}
            <ClientDetailView
              client={client}
              alertMessage={
                !client.dimensaAnalysis ? 'Realize as consultas dos sócios na Dimensa' : undefined
              }
              onOpenDocument={handleOpenDocument}
            />

            {/* Business Rules Button */}
            <div className="flex justify-end mb-6">
              <button
                type="button"
                onClick={() => {
                  setIsBusinessRulesOpen(true);
                }}
                className="p-2 text-purple-600 hover:bg-purple-50 rounded-full transition-colors"
                title="Regras de Negócio"
              >
                <FiHelpCircle className="w-5 h-5" />
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => {
                  setIsBlocklistModalOpen(true);
                }}
                className="flex items-center gap-2 px-6 py-3 border-2 border-red-600 text-red-600 rounded-lg font-medium hover:bg-red-50 transition-colors"
              >
                <FiShield className="w-5 h-5" />
                Adicionar a Blocklist
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsRejectModalOpen(true);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
              >
                <FiXCircle className="w-5 h-5" />
                Reprovar conta
              </button>
              <button
                type="button"
                onClick={() => {
                  void navigate(`/onboarding/dimensa/${clientId ?? ''}`);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
              >
                Realizar consulta Dimensa
              </button>
            </div>
          </div>
        </main>
      </div>

      {/* Blocklist Modal */}
      <BlocklistModal
        isOpen={isBlocklistModalOpen}
        onClose={() => {
          setIsBlocklistModalOpen(false);
        }}
        onConfirm={handleBlocklistConfirm}
        clientName={client.aliasName ?? client.name}
      />

      {/* Reject Modal */}
      <RejectModal
        isOpen={isRejectModalOpen}
        onClose={() => {
          setIsRejectModalOpen(false);
        }}
        onConfirm={handleRejectConfirm}
        clientName={client.aliasName ?? client.name}
      />

      {/* Document Modal */}
      <DocumentModal
        isOpen={isDocumentModalOpen}
        onClose={() => {
          setIsDocumentModalOpen(false);
        }}
        documentTitle={currentDocument.title}
        documentUrl={currentDocument.url}
        isLoading={isLoadingDocument}
      />

      {/* Business Rules Drawer */}
      <BusinessRulesDrawer
        isOpen={isBusinessRulesOpen}
        onClose={() => {
          setIsBusinessRulesOpen(false);
        }}
      />
    </div>
  );
};
